import React, { useEffect, useMemo, useState } from "react";

import useYardActionEvents from "@hsl/core/hooks/useYardAnalyticsEvents";
import cx from "@hsl/core/utils/cx";
import useFCSubscriptionStore from "@hsl/fc-subscription/store";
import {
    type FundDataWithType,
    type ShareClassDataWithType,
} from "@hsl/fund-explorer/metaSchemas";
import { Translate } from "@hsl/lgim-fund-page/src/hooks/useTranslation";

import SvgUserIcon from "../../icons/User";
import { SubscribeIcon, WatchIcon } from "./SubscriptionButtons";

declare global {
    interface Window {
        enableFCS?: boolean;
    }
}

type Props = {
    url: URL;
    fund: FundDataWithType;
    shareClasses?: ShareClassDataWithType[];
};

export const FundPageButtons = ({ url, fund, shareClasses }: Props) => {
    const [isLoading, setIsLoading] = useState(true);
    const { buttonClickEvent } = useYardActionEvents();
    const initSubscriptionsStore = useFCSubscriptionStore(
        (state) => state.init,
    );
    const subscriptions = useFCSubscriptionStore(
        (state) => state.userSubscriptions,
    );
    const toggleItem = useFCSubscriptionStore(
        (state) => state.toggleBasketItem,
    );
    const setBasketMode = useFCSubscriptionStore(
        (state) => state.setBasketMode,
    );
    const clearBasket = useFCSubscriptionStore((state) => state.clearBasket);
    const confirmedSubscriptions = subscriptions.filter(
        (s) => s.subscriptionStatus === "CONFIRMED",
    );
    const subsFundId = `${fund?.type}-${fund?.data.id}`;
    const isWatching = confirmedSubscriptions.some(
        (s) => s.fundId === subsFundId && s.subscriptionType === "WATCHLIST",
    );
    const isSubscribed = confirmedSubscriptions.some(
        (s) => s.fundId === subsFundId && s.subscriptionType === "FULL",
    );

    const canManage = isSubscribed || isWatching;
    const manageURL = (splitOn: string, tail: string) => {
        const mURL = new URL(url);
        const [head] = mURL.pathname.split(splitOn);
        mURL.pathname = [head, tail].join(splitOn);
        return mURL.toString();
    };
    const btnClasses =
        "rounded px-3 py-2 flex gap-2 items-center border border-taupe-grey";
    const handleClick =
        (mode: "subscribe" | "watch") => (evt: React.MouseEvent) => {
            evt.stopPropagation();
            const doNothing = mode === "subscribe" ? isSubscribed : isWatching;
            if (doNothing) {
                return;
            }
            clearBasket();
            setBasketMode(mode);
            toggleItem(fund, shareClasses);
            buttonClickEvent({
                name: mode === "subscribe" ? "Subscribe" : "Add to watchlist",
                fund: {
                    name: fund.data.data.name,
                    id: fund.data.id,
                    type: fund.type,
                },
                details: "Subscription options",
            });
            window.location.href = url?.toString();
        };

    const enableFCS = useMemo(() => {
        if (typeof window !== "undefined") {
            if (!("enableFCS" in window))
                console.warn(
                    "enableFCS not set, defaulting to showing the FCS service. To disable, set `window.enableFCS = false`.",
                );
            return window.enableFCS ?? true;
        }
        return true;
    }, []);

    useEffect(() => {
        if (enableFCS) {
            initSubscriptionsStore({
                OpenAPIBase: `${window.location.origin}/srp/api/fcs`,
            })
                .catch((err) =>
                    console.error(
                        "Error initialising subscriptions store",
                        err,
                    ),
                )
                .finally(() => setIsLoading(false));
        }
    }, [initSubscriptionsStore, enableFCS]);

    if (!enableFCS) return null;

    return (
        !isLoading && (
            <div className="mt-12 max-sm:mx-4">
                {canManage ? (
                    <a
                        href={manageURL("fund-centre", "/my-funds/manage")}
                        className={cx(
                            btnClasses,
                            "bg-core-blue border-0 text-white",
                        )}
                    >
                        <SvgUserIcon fill="white" width={16} height={16} />{" "}
                        <Translate>Manage your funds</Translate>
                    </a>
                ) : (
                    <div className="flex flex-col gap-4">
                        <button
                            onClick={handleClick("subscribe")}
                            className={cx(btnClasses, "hover:bg-[#ffd50011]")}
                        >
                            <SubscribeIcon active />{" "}
                            <Translate>Subscribe</Translate>
                        </button>
                        <button
                            onClick={handleClick("watch")}
                            className={cx(btnClasses, "hover:bg-[#ffd50011]")}
                        >
                            <WatchIcon active />{" "}
                            <Translate>Add to watchlist</Translate>
                        </button>
                    </div>
                )}
            </div>
        )
    );
};
